<script setup>
import {computed, ref} from "vue";
import DropdownRow from "@/components/global-themed/inputs/dropdown/DropdownRow.vue";
import BaseDropdown from "@/components/global-themed/inputs/dropdown/BaseDropdown.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {useStore} from "vuex";
import {filterOutActionFromQueryDataById} from "@/composables/useActions";
import {errorToast, successToast} from "@/composables/useToasts";

defineProps({
  action: {
    type: Object,
    required: true,
  },
});

const showDropdown = ref(false);
const queryClient = useQueryClient();

const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);

const {mutate: hideAction} = useMutation({
  mutationFn:  actionId => axios.post(`/ajax/v2/actions/${actionId}/hide`),
  onMutate: async (actionId) => {
    await queryClient.cancelQueries({ queryKey: ['hidden-actions'] });
    await queryClient.cancelQueries({ queryKey: ['actions'] });

    successToast('Action hidden');

    window.mixpanel('Actions v2: Hide action', {
      'action_id': actionId,
    });

    return filterOutActionFromQueryDataById(activeTab, queryClient, actionId);
  },
  onError: (error, _, context) => {
    const {message} = error.response.data;
    errorToast(message ?? 'Something went wrong hiding action');

    queryClient.setQueryData(['actions'], context.previousActions);
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['actions'] });
    queryClient.invalidateQueries({ queryKey: ['hidden-actions'] });
    queryClient.invalidateQueries({ queryKey: ['action-categories'] });
  },
});

const {mutate: hideActionTemplate} = useMutation({
  mutationFn:  actionTemplateId => axios.post(`/ajax/v2/actions/template/${actionTemplateId}/hide`),
  onMutate: async (actionTemplateId) => {
    await queryClient.cancelQueries({ queryKey: ['hidden-actions-by-template'] });
    await queryClient.cancelQueries({ queryKey: ['actions'] });

    successToast('Actions hidden for type');

    window.mixpanel('Actions v2: Hide actions of type', {
      'action_template_id': actionTemplateId,
    });

    return filterOutActionFromQueryDataById(activeTab, queryClient, actionTemplateId, 'action_template_id');
  },
  onError: (error, _, context) => {
    const {message} = error.response.data;
    errorToast(message ?? 'Something went wrong hiding action for category');

    queryClient.setQueryData(['actions', activeTab], context.previousActions);
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['actions'] });
    queryClient.invalidateQueries({ queryKey: ['hidden-actions-by-template'] });
    queryClient.invalidateQueries({ queryKey: ['action-categories'] });
  },
});
</script>

<template>
  <BaseDropdown
    v-model:dropdownOpen="showDropdown"
  >
    <template #trigger="{ toggleDropdown }">
      <div
        class="tw-flex tw-cursor-pointer"
        @click="toggleDropdown"
      >
        <div
          class="tw-p-3 hover:tw-bg-grey-highlight tw-rounded-xl tw-rounded-r-none tw-transition-colors tw-duration-150"
          :class="['tw-border-2', showDropdown ? 'tw-border-blue' : 'tw-border-grey-mid']"
        >
          <svg-vue
            icon="icons/eye-hidden"
            class="tw-h-6 tw-w-6 tw-text-grey-dark"
          />
        </div>
        <div
          class="tw-border-2 hover:tw-bg-grey-highlight tw-border-l-0 tw-flex tw-items-center tw-px-2 tw-rounded-l-none tw-rounded-xl tw-transition-colors tw-duration-150"
          :class="['tw-border-2', showDropdown ? 'tw-border-blue' : 'tw-border-grey-mid']"
        >
          <svg-vue
            icon="icons/chevron-thick"
            class="tw-h-3 tw-w-3"
            :class="showDropdown ? 'tw-text-blue' : 'tw-text-grey'"
          />
        </div>
      </div>
    </template>

    <template #content>
      <DropdownRow @click="hideAction(action.id)">
        <svg-vue
          class="tw-text-grey tw-w-6 tw-flex-shrink-0"
          icon="icons/todo"
        />
        <span>Hide Individual Action</span>
      </DropdownRow>

      <DropdownRow @click="hideActionTemplate(action.action_template_id)">
        <svg-vue
          class="tw-text-grey tw-w-6 tw-flex-shrink-0"
          icon="icons/browser-x"
        />
        <span>Hide Actions For Type</span>
      </DropdownRow>
    </template>
  </BaseDropdown>
</template>
